import React, { useCallback, useEffect, useState } from 'react';
import { Box, Button, Dialog, DialogContent, DialogTitle, IconButton, Skeleton, Stack, Typography } from '@mui/material';
import { Close } from '@mui/icons-material';
import { format } from 'date-fns';
import { useSnackbar } from 'notistack';
import { timetableService, HistoryItem } from '../../../services/timetableService';
import { ToneChip } from '../../common/StatusChip';
import ConfirmationDialog from '../../common/ConfirmationDialog';

interface HistoryDialogProps {
  open: boolean;
  onClose: () => void;
  sessionId: string;
  canRestore: boolean;
  hasDraft: boolean;
  onRestored: () => void;
}

const PAGE = 10;

/** Timetables published this session, newest first; any can be copied back into a draft. */
const HistoryDialog: React.FC<HistoryDialogProps> = ({ open, onClose, sessionId, canRestore, hasDraft, onRestored }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [items, setItems] = useState<HistoryItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [restoring, setRestoring] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<HistoryItem | null>(null);

  const load = useCallback(async (page: number) => {
    setLoading(true);
    try {
      const res = await timetableService.history(sessionId, page, PAGE);
      setItems((prev) => (page === 1 ? res.items : [...prev, ...res.items]));
      setTotal(res.total);
    } catch (err: any) {
      enqueueSnackbar(err?.message || 'Could not load history', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [sessionId, enqueueSnackbar]);

  useEffect(() => { if (open) load(1); }, [open, load]);

  const restore = async (item: HistoryItem) => {
    setConfirming(null);
    setRestoring(item._id);
    try {
      await timetableService.createDraft(sessionId, item._id, true);
      enqueueSnackbar('Copied into a draft', { variant: 'success' });
      onRestored();
      onClose();
    } catch (err: any) {
      enqueueSnackbar(err?.message || 'Could not restore', { variant: 'error' });
    } finally {
      setRestoring(null);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center' }}>
        <Box sx={{ flex: 1 }}>History</Box>
        <IconButton size="small" onClick={onClose} aria-label="Close"><Close fontSize="small" /></IconButton>
      </DialogTitle>
      <DialogContent>
        {loading && !items.length ? (
          [0, 1, 2].map((i) => <Skeleton key={i} variant="rounded" height={56} sx={{ mb: 1, borderRadius: '10px' }} />)
        ) : !items.length ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>Nothing has been published this session yet.</Typography>
        ) : (
          <Stack spacing={1}>
            {items.map((item) => (
              <Box key={item._id} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 1.75, py: 1.25, border: '1px solid', borderColor: 'divider', borderRadius: '10px' }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="body2" fontWeight={650}>
                      {item.publishedAt ? format(new Date(item.publishedAt), 'd MMM yyyy, h:mm a') : 'Unpublished'}
                    </Typography>
                    {item.status === 'published' && <ToneChip tone="success" label="Live" />}
                  </Stack>
                  <Typography variant="caption" color="text.secondary" noWrap component="div">
                    {[item.note, item.publishedBy?.name && `by ${item.publishedBy.name}`].filter(Boolean).join(' · ') || ' '}
                  </Typography>
                </Box>
                {canRestore && (
                  <Button size="small" onClick={() => (hasDraft ? setConfirming(item) : restore(item))} disabled={!!restoring} sx={{ textTransform: 'none', flexShrink: 0 }}>
                    {restoring === item._id ? 'Copying…' : 'Copy to draft'}
                  </Button>
                )}
              </Box>
            ))}
            {items.length < total && (
              <Button onClick={() => load(Math.floor(items.length / PAGE) + 1)} disabled={loading} sx={{ textTransform: 'none' }}>Show more</Button>
            )}
          </Stack>
        )}
      </DialogContent>
      <ConfirmationDialog
        open={!!confirming}
        onClose={() => setConfirming(null)}
        onConfirm={() => confirming && restore(confirming)}
        title="Replace your draft?"
        message="Copying this timetable replaces the draft you are working on."
        confirmText="Replace draft"
      />
    </Dialog>
  );
};

export default HistoryDialog;
